import { createReducer } from '@reduxjs/toolkit';

import { Roles } from '../../constants';
import { DevicesSortBy, FetchStatus, SortType, UsersSortBy } from '../../enums';
import * as actions from './actions';
import {
  IOrganizationDevicesSort,
  IOrganizationState,
  IOrganizationUsersRoleFilter,
  IOrganizationUsersSort,
} from './types';

export const roleFilterInitialState = Roles.reduce(
  (acc, key) => ({
    ...acc,
    [key]: false,
  }),
  {} as IOrganizationUsersRoleFilter,
);

export const devicesSortInitialState: IOrganizationDevicesSort = {
  by: DevicesSortBy.SERIAL_NUMBER,
  type: SortType.IDLE,
};

export const usersSortInitialState: IOrganizationUsersSort = {
  by: UsersSortBy.NAME,
  type: SortType.IDLE,
};

export const initialState: IOrganizationState = {
  devices: [],
  users: [],
  status: {
    organization: FetchStatus.IDLE,
    fetchDevices: FetchStatus.IDLE,
    removeDevice: FetchStatus.IDLE,
    registerDevices: FetchStatus.IDLE,
    orderDevices: FetchStatus.IDLE,
    fetchUsers: FetchStatus.IDLE,
    createUser: FetchStatus.IDLE,
    changeUserRole: FetchStatus.IDLE,
    deleteUser: FetchStatus.IDLE,
  },
  searchText: {
    devices: '',
    users: '',
  },
  filters: {
    role: roleFilterInitialState,
  },
  sort: {
    devices: devicesSortInitialState,
    users: usersSortInitialState,
  },
};

export const organizationReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(actions.resetOrganization, () => initialState)
    .addCase(actions.resetRemoveOrganizationDeviceStatus, (state) => {
      state.status.removeDevice = FetchStatus.IDLE;
    })
    .addCase(actions.resetRegisterOrganizationDevicesStatus, (state) => {
      state.status.registerDevices = FetchStatus.IDLE;
    })
    .addCase(actions.resetOrderOrganizationDevicesStatus, (state) => {
      state.status.orderDevices = FetchStatus.IDLE;
    })
    .addCase(actions.resetCreateOrganizationUserStatus, (state) => {
      state.status.createUser = FetchStatus.IDLE;
    })
    .addCase(actions.resetChangeOrganizationUserRoleStatus, (state) => {
      state.status.changeUserRole = FetchStatus.IDLE;
    })
    .addCase(actions.resetDeleteOrganizationUserStatus, (state) => {
      state.status.deleteUser = FetchStatus.IDLE;
    })
    .addCase(actions.updateOrganizationDevicesSearchText, (state, action) => {
      state.searchText.devices = action.payload.searchText;
    })
    .addCase(actions.updateOrganizationDevicesSort, (state, action) => {
      state.sort.devices = {
        ...state.sort.devices,
        ...action.payload.sort,
      };
    })
    .addCase(actions.updateOrganizationUsersSearchText, (state, action) => {
      state.searchText.users = action.payload.searchText;
    })
    .addCase(actions.updateOrganizationUsersRoleFilter, (state, action) => {
      state.filters.role = {
        ...state.filters.role,
        ...action.payload.filters,
      };
    })
    .addCase(actions.updateOrganizationUsersSort, (state, action) => {
      state.sort.users = {
        ...state.sort.users,
        ...action.payload.sort,
      };
    })
    .addCase(actions.fetchOrganization, (state) => {
      state.status.organization = FetchStatus.LOADING;
    })
    .addCase(actions.fetchOrganizationSucceeded, (state, action) => {
      state.status.organization = FetchStatus.SUCCESS;
      state.organization = action.payload.organization;
    })
    .addCase(actions.fetchOrganizationFailed, (state) => {
      state.status.organization = FetchStatus.FAILURE;
    })
    .addCase(actions.fetchOrganizationDevices, (state) => {
      state.status.fetchDevices = FetchStatus.LOADING;
    })
    .addCase(actions.fetchOrganizationDevicesSucceeded, (state, action) => {
      state.status.fetchDevices = FetchStatus.SUCCESS;
      state.devices = action.payload.devices.devices;
    })
    .addCase(actions.fetchOrganizationDevicesFailed, (state) => {
      state.status.fetchDevices = FetchStatus.FAILURE;
    })
    .addCase(actions.removeOrganizationDevice, (state) => {
      state.status.removeDevice = FetchStatus.LOADING;
    })
    .addCase(actions.removeOrganizationDeviceSucceeded, (state, action) => {
      state.status.removeDevice = FetchStatus.SUCCESS;
      state.devices = action.payload.devices.devices;
    })
    .addCase(actions.removeOrganizationDeviceFailed, (state) => {
      state.status.removeDevice = FetchStatus.FAILURE;
    })
    .addCase(actions.registerOrganizationDevices, (state) => {
      state.status.registerDevices = FetchStatus.LOADING;
    })
    .addCase(actions.registerOrganizationDevicesSucceeded, (state) => {
      state.status.registerDevices = FetchStatus.SUCCESS;
    })
    .addCase(actions.registerOrganizationDevicesFailed, (state) => {
      state.status.registerDevices = FetchStatus.FAILURE;
    })
    .addCase(actions.orderOrganizationDevices, (state) => {
      state.status.orderDevices = FetchStatus.LOADING;
    })
    .addCase(actions.orderOrganizationDevicesSucceeded, (state) => {
      state.status.orderDevices = FetchStatus.SUCCESS;
    })
    .addCase(actions.orderOrganizationDevicesFailed, (state) => {
      state.status.orderDevices = FetchStatus.FAILURE;
    })
    .addCase(actions.fetchOrganizationUsers, (state) => {
      state.status.fetchUsers = FetchStatus.LOADING;
    })
    .addCase(actions.fetchOrganizationUsersSucceeded, (state, action) => {
      state.status.fetchUsers = FetchStatus.SUCCESS;
      state.users = action.payload.users.users;
    })
    .addCase(actions.fetchOrganizationUsersFailed, (state) => {
      state.status.fetchUsers = FetchStatus.FAILURE;
    })
    .addCase(actions.createOrganizationUser, (state) => {
      state.status.createUser = FetchStatus.LOADING;
    })
    .addCase(actions.createOrganizationUserSucceeded, (state, action) => {
      state.status.createUser = FetchStatus.SUCCESS;
      state.users = action.payload.users.users;
    })
    .addCase(actions.createOrganizationUserFailed, (state) => {
      state.status.createUser = FetchStatus.FAILURE;
    })
    .addCase(actions.changeOrganizationUserRole, (state) => {
      state.status.changeUserRole = FetchStatus.LOADING;
    })
    .addCase(actions.changeOrganizationUserRoleSucceeded, (state, action) => {
      state.status.changeUserRole = FetchStatus.SUCCESS;
      state.users = action.payload.users.users;
    })
    .addCase(actions.changeOrganizationUserRoleFailed, (state) => {
      state.status.changeUserRole = FetchStatus.FAILURE;
    })
    .addCase(actions.deleteOrganizationUser, (state) => {
      state.status.deleteUser = FetchStatus.LOADING;
    })
    .addCase(actions.deleteOrganizationUserSucceeded, (state, action) => {
      state.status.deleteUser = FetchStatus.SUCCESS;
      state.users = action.payload.users.users;
    })
    .addCase(actions.deleteOrganizationUserFailed, (state) => {
      state.status.deleteUser = FetchStatus.FAILURE;
    });
});
